import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { ResourceProvider } from "../../contexts/ResourceContext";
import withAuthentication from "../../hocs/withAuthentication";
import { fontSizes } from "../../theme";
import { TextInput } from "../../components/inputs";
import { FormResponse, schema, FormData, ClinicDetailsResponse } from "./types";
import SubmitButton from "../../components/SubmitButton";
import Members from "./Members";
import DeleteClinic from "./DeleteClinic";
import { useNavigate } from "react-router-dom";

const Page: React.FC = () => {
  const { user } = useAuth();
  const { apiService, setLoading, setSubmitting } = useApi();
  const { updateActions, updateBreadcrumbs } = useApp();

  const [clinicName, setClinicName] = useState("");
  const [errors, setErrors] = useState<any>({});

  const { translate } = useApp();
  const translations = translate("manage");

  const { showSuccess, showError } = useError();
  const navigate = useNavigate();

  useEffect(() => {
    updateActions([]);
    updateBreadcrumbs([{ label: translations.breadcrumbs.manage }]);
    fetchClinicDetails();

    return () => {
      updateBreadcrumbs([]);
    };
  }, []);

  const [savedClinicName, setSavedClinicName] = useState("");

  const fetchClinicDetails = async () => {
    try {
      const response = await apiService.get<ClinicDetailsResponse>("/clinic");
      setClinicName(response.name);
      setSavedClinicName(response.name);
    } catch (error: any) {
      showError(error.message);
    }
  };

  const getUpdatePayload = () => {
    return {
      fields: {
        name: clinicName,
      },
    };
  };

  const validateDetails = () => {
    const payload = getUpdatePayload();
    const result = schema.safeParse(payload);
    if (!result.success) {
      const error = translate("manage", result.error.errors[0].message);
      setErrors({ clinicName: error });
      return false;
    }
    return true;
  };

  const handleSaveDetails = async () => {
    if (!validateDetails()) {
      return;
    }

    try {
      setSubmitting(true);
      const payload = getUpdatePayload();
      const response = await apiService.post<FormResponse, FormData>(
        "/clinic",
        payload
      );
      if (response.clinic?.updated) {
        showSuccess(translations.sections.clinic_details.success);
        setSavedClinicName(clinicName);
      } else {
        showError(translations.errors.general_error);
      }
    } catch (error: any) {
      showError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCustomize = () => {
    navigate("/report");
  };

  return (
    <ResourceProvider>
      <Container>
        <Typography fontSize={fontSizes["2xl"]} variant={"medium"}>
          {translations.title}
        </Typography>

        <Stack spacing={2}>
          <Card sx={{ py: 2, height: "100%" }}>
            <Stack spacing={1}>
              <Box px={4}>
                <Typography variant={"medium"}>
                  {translations.sections.clinic_details.title}
                </Typography>
              </Box>
              <Stack px={4} direction="row" spacing={2} alignItems="center">
                <Box width={"100%"}>
                  <TextInput
                    fullWidth
                    label={translations.sections.clinic_details.fields.name}
                    value={clinicName}
                    onChange={(value: string | number) =>
                      setClinicName(value as string)
                    }
                    error={errors.clinicName}
                    helperText={errors.clinicName}
                  />
                </Box>
                <Box width={60}>
                  <SubmitButton onClick={handleSaveDetails}>
                    {translations.sections.clinic_details.buttons.save}
                  </SubmitButton>
                </Box>
              </Stack>
              <Divider
                variant="middle"
                orientation="horizontal"
                sx={{ width: "100%" }}
              />
              <Stack px={4} direction={"row"} justifyContent={"space-between"}>
                <Stack spacing={0}>
                  <Typography variant={"medium"}>
                    {translations.sections.customize.title}
                  </Typography>
                  <Typography fontSize={"sm"} variant={"subtitle1"}>
                    {translations.sections.customize.subtitle}
                  </Typography>
                </Stack>

                <Box width={160}>
                  <Button
                    fullWidth
                    color={"greyButton"}
                    onClick={handleCustomize}
                  >
                    {translations.sections.customize.buttons.customize}
                  </Button>
                </Box>
              </Stack>
            </Stack>
          </Card>
          <Card sx={{ p: 4 }}>
            <Members clinicName={savedClinicName} />
          </Card>
          <DeleteClinic clinicName={savedClinicName} />
        </Stack>
      </Container>
    </ResourceProvider>
  );
};

export default withAuthentication(Page);
